import { SpriteRenderer } from '../components/SpriteRenderer';
import { SystemStage, type System } from '../core/System';
import { WorldTransform } from '../entities/WorldTransform';
import { Color } from '../math/Color';
import { canvasSizeEquals, type CanvasSize, type CanvasSizeSource } from '../rendering/CanvasSize';
import { type Camera } from '../rendering/Camera';
import { type Renderer, type SpriteDrawCall } from '../rendering/Renderer';
import { fillSpriteQuad } from '../rendering/SpriteQuad';
import { type Texture } from '../rendering/Texture';
import { computeViewport, DESIGN_HEIGHT, type Viewport } from '../rendering/Viewport';
import { type Scene } from '../scene/Scene';

/** Where a {@link SpriteRenderer}'s asset key is resolved into a texture. `AssetManager` satisfies it. */
export interface TextureProvider {
  /** The texture loaded under this key, or undefined when nothing was loaded for it. */
  getTexture(key: string): Texture | undefined;
}

/** Construction options for {@link RenderSystem}. */
export interface RenderSystemOptions {
  /** Backend the sprites are submitted to. */
  readonly renderer: Renderer;
  /** Where the canvas' current size is read from. */
  readonly surface: CanvasSizeSource;
  /** Where texture keys are resolved. Without it, sprites draw as flat colored quads. */
  readonly textures?: TextureProvider | undefined;
  /** Overrides {@link DESIGN_HEIGHT} for products that author against a different vertical extent. */
  readonly designHeight?: number | undefined;
}

/**
 * Turns the scene's sprites into draw calls, once per frame.
 *
 * This is the only place the component model and the graphics backend meet. It reads
 * {@link SpriteRenderer} data, folds in each entity's world transform and submits the result; what a
 * quad really is on the GPU is entirely the renderer's business.
 *
 * It also owns the resize handshake. The canvas size is sampled once per frame rather than through a
 * resize event, so a resize always takes effect at the same point in the frame instead of landing
 * halfway through one.
 *
 * Texture keys are resolved here, at the last possible moment, so a sprite whose art is still loading
 * draws as its flat color and starts showing the image on the frame after it lands, with nothing to
 * notify and nothing to retry.
 */
export class RenderSystem implements System {
  /** Rendering happens after all gameplay stages and before debug draw. */
  readonly stage = SystemStage.Render;

  readonly #renderer: Renderer;
  readonly #surface: CanvasSizeSource;
  readonly #textures: TextureProvider | undefined;
  readonly #designHeight: number;
  readonly #worldTransform = new WorldTransform();
  readonly #drawCall: SpriteDrawCall = {
    texture: undefined,
    color: Color.white(),
    x: 0,
    y: 0,
    rotation: 0,
    width: 0,
    height: 0,
    anchorX: 0,
    anchorY: 0,
    sortOrder: 0,
  };

  #viewport: Viewport;
  #lastSize: CanvasSize | undefined;

  constructor(options: RenderSystemOptions) {
    this.#renderer = options.renderer;
    this.#surface = options.surface;
    this.#textures = options.textures;
    this.#designHeight = options.designHeight ?? DESIGN_HEIGHT;
    this.#viewport = computeViewport(options.surface.size.cssWidth, options.surface.size.cssHeight, this.#designHeight);
  }

  /** Submits every visible sprite in the scene and presents the frame. */
  onUpdate(scene: Scene, _deltaSeconds: number): void {
    this.#syncSize(scene.camera);

    this.#renderer.beginFrame(scene.camera);
    for (const entity of scene.getEntitiesWith(SpriteRenderer)) {
      const sprite = entity.getComponent(SpriteRenderer);
      if (sprite === undefined || !sprite.visible || !entity.activeInHierarchy) {
        continue;
      }
      entity.getWorldTransform(this.#worldTransform);
      this.#renderer.drawSprite(entity.id, this.#fillDrawCall(sprite));
    }
    this.#renderer.endFrame();
  }

  /*
   * The canvas size and the camera are tracked separately, because they go out of date for unrelated
   * reasons: the size changes when the window does, and the camera changes when a scene is activated,
   * bringing a fresh one that has never been told what the viewport is. Syncing only on a size change
   * left a switched-to scene projecting through the default viewport, stretched, until the window
   * happened to be resized.
   */
  #syncSize(camera: Camera): void {
    const size = this.#surface.size;
    if (this.#lastSize === undefined || !canvasSizeEquals(size, this.#lastSize)) {
      this.#lastSize = size;
      this.#viewport = computeViewport(size.cssWidth, size.cssHeight, this.#designHeight);
      this.#renderer.resize(size, this.#viewport);
    }

    if (camera.viewport !== this.#viewport) {
      camera.setViewport(this.#viewport);
    }
  }

  /*
   * One draw call struct is refilled for every sprite. The renderer's contract says it copies what it
   * needs, which is what makes a single reused instance safe and the render loop allocation free.
   */
  #fillDrawCall(sprite: SpriteRenderer): SpriteDrawCall {
    const drawCall = this.#drawCall;

    fillSpriteQuad(drawCall, sprite, this.#worldTransform);
    drawCall.texture = sprite.texture === undefined ? undefined : this.#textures?.getTexture(sprite.texture);
    drawCall.color = sprite.color;
    drawCall.sortOrder = sprite.sortOrder;
    return drawCall;
  }
}
